import { NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { CountriesCurrenciesService } from '@app/finxone-web-frontend/app/lib/services/countries-currencies-service/countries-currencies.service';
import { AvailableSavingsProductType } from '@app/finxone-web-frontend/app/lib/services/savings-account-service/savings-account.type';
import { scrollLoading } from '@app/finxone-web-frontend/app/lib/utils/scrolling-utils/scrolling.utils';
import {
  buildHandleBarModel,
  GenericHandleBarModel,
} from '@app/finxone-web-frontend/app/lib/utils/template/model-builder.utils';
import { PageConfigurationProvider } from '@app/finxone-web-frontend/app/Modules/zones/page-configuration.provider';
import { AutoDestroy, UUID } from '@finxone-platform/shared/base-types';
import { IconCacheService } from '@finxone-platform/shared/services';
import {
  baseCssConfigStyle,
  ProviderClass,
  resolveSkeletonLoadingTemplate,
  SKELETON_LAYOUT_CONFIG_PATH,
  UiZoneWidgetAttributeConfig,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import Handlebars from 'handlebars';
import { Subject, takeUntil } from 'rxjs';
import { SafeHtmlPipe } from '../../../../../lib/pipes/SafeHtml.pipe';
import { ListItemComponent } from '../list-item/list-item.component';

@Component({
  selector: 'finxone-web-frontend-products-list',
  templateUrl: './products-list.component.html',
  imports: [NgTemplateOutlet, SafeHtmlPipe, ListItemComponent],
})
export class ProductsListComponent implements AfterViewInit {
  @AutoDestroy private readonly destroy$: Subject<void> = new Subject<void>();
  @Input() public contentStyle: baseCssConfigStyle;
  @Input() public searchIconStyle: baseCssConfigStyle;
  @Input() public textContentStyle: baseCssConfigStyle;
  @Input() public productsList: AvailableSavingsProductType[];
  @Input() public isApiInProgress = false;
  @Input() public isWorkflow = false;
  @Input() public appBuilderState = 'default';
  @Input() public selectedProductCode = '';
  @Input() public set widgetAttribute(value: UiZoneWidgetAttributeConfig) {
    this.widgetAttributes = value;
    this.setupWidgetDataFromAttributes();
  }
  public widgetAttributes: UiZoneWidgetAttributeConfig;

  public fetchingTemplate = '';
  public noRecordsTemplate = '';
  public content = '';
  public containerHeight = '';

  public templateFunction: HandlebarsTemplateDelegate;
  public noRecordsTemplateFunction: HandlebarsTemplateDelegate;
  public fetchingTemplateFunction: HandlebarsTemplateDelegate;
  public parentElementId = 'products-list-' + new UUID().toString();

  @Output() public productClicked = new EventEmitter<AvailableSavingsProductType>();
  @Output() public scrolled = new EventEmitter<Event>();

  private baseHandleBarModel: GenericHandleBarModel;

  constructor(
    private readonly store: Store,
    private readonly countriesCurrenciesService: CountriesCurrenciesService,
    private readonly iconCacheService: IconCacheService,
    private readonly el: ElementRef,
    public pageConfig: PageConfigurationProvider,
    public dataIn: ProviderClass<UiZoneWidgetAttributeConfig>,
    public configService: ConfigService,
    private readonly ref: ChangeDetectorRef,
  ) {}

  private setupWidgetDataFromAttributes(): void {
    this.initHandleBars();
  }

  ngAfterViewInit(): void {
    requestAnimationFrame(() => {
      this.evaluateListContainerElementHeight();
    });
  }

  private initHandleBars() {
    this.content = this.widgetAttributes.widgetProperties.textContent;
    this.noRecordsTemplate = this.widgetAttributes['noRecordsTemplate'] ?? 'no products found';
    this.fetchingTemplate =
      this.widgetAttributes.widgetProperties['loadingTextContent'] ?? 'fetching products...';
    this.templateFunction = Handlebars.compile(this.content);
    this.noRecordsTemplateFunction = Handlebars.compile(this.noRecordsTemplate);
    this.fetchingTemplateFunction = Handlebars.compile(this.fetchingTemplate);
    buildHandleBarModel(
      this.store,
      '',
      this.countriesCurrenciesService,
      this.iconCacheService,
      undefined,
      undefined,
      undefined,
      undefined,
      true, // to avoid auto refresh of state data (accounts listing etc)
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe((model) => {
        this.baseHandleBarModel = model;
      });
  }

  private evaluateListContainerElementHeight() {
    this.containerHeight = scrollLoading(this.parentElementId, this.el);
    this.ref.detectChanges();
  }

  public renderNoRecordsTemplate() {
    return this.noRecordsTemplateFunction({
      ...this.baseHandleBarModel,
    });
  }

  public renderFetchingTemplate() {
    // The designed skeleton wins over the widget's own fetching template, so the live app and the
    // skeleton designer render the same loading state from one config.
    return resolveSkeletonLoadingTemplate(
      this.widgetAttributes?.[SKELETON_LAYOUT_CONFIG_PATH],
      this.fetchingTemplateFunction({
        ...this.baseHandleBarModel,
      }),
    );
  }

  public onProductClick(product: AvailableSavingsProductType) {
    this.productClicked.emit(product);
  }

  public productHandleBarModel(product: AvailableSavingsProductType, index: number) {
    return {
      ...this.baseHandleBarModel,
      ...product,
      index,
    };
  }

  public onScroll(event: Event) {
    this.scrolled.emit(event);
  }

  public getItemCustomClasses() {
    // a linked list takes each row's box from the theme's List styling; it drops `product-list-item`
    // because the widget's default custom CSS targets it with an id-scoped (higher specificity) rule
    const isGlobalStylingEnabled = !!this.widgetAttributes?.widgetProperties?.['globalStyling'];
    const classes = [
      'mb-5',
      'pointer-events-none',
      isGlobalStylingEnabled ? 'global-list-styling' : 'product-list-item',
    ];
    return classes.join(' ');
  }
}
